import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "@/utils/cx";

/**
 * Card / CardHeader / CardBody / CardFooter — slotový bordered surface na UUI tokenech.
 *
 * Nativní TableCard je vázán jen na tabulky; obecný surface chybí. Wrapper zachovává
 * legacy { title, actions, children } CardHeader API. Surface: rounded-xl bg-primary
 * ring-1 ring-secondary shadow-xs; header/footer border-secondary.
 */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
}

export function Card({ className, children, ...rest }: CardProps) {
    return (
        <div
            className={cx("flex flex-col rounded-xl bg-primary shadow-xs ring-1 ring-secondary", className)}
            {...rest}
        >
            {children}
        </div>
    );
}

export interface CardHeaderProps extends HTMLAttributes<HTMLElement> {
    title?: string;
    actions?: ReactNode;
    children?: ReactNode;
}

export function CardHeader({ title, actions, className, children, ...rest }: CardHeaderProps) {
    return (
        <header
            className={cx("flex items-center justify-between gap-4 border-b border-secondary px-5 py-4", className)}
            {...rest}
        >
            {children ?? (
                <>
                    {title && <h2 className="m-0 text-md font-semibold text-primary">{title}</h2>}
                    {actions && <div>{actions}</div>}
                </>
            )}
        </header>
    );
}

export function CardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cx("p-5", className)} {...rest}>
            {children}
        </div>
    );
}

export function CardFooter({ className, children, ...rest }: HTMLAttributes<HTMLElement>) {
    return (
        <footer
            className={cx("flex justify-end gap-2 border-t border-secondary px-5 py-4", className)}
            {...rest}
        >
            {children}
        </footer>
    );
}
